import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, UserPlus, KeyRound, Ban, CheckCircle2, Trash2 } from "lucide-react";
import { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const LABEL = { superadmin: "Super Admin", admin: "Admin", staff: "Pekerja", sale: "Sale (Lihat Jualan Sahaja)", pending: "Menunggu Kelulusan" };
const inputCls = "w-full h-10 px-3 rounded-lg border text-sm";
const btnCls = "h-10 px-5 rounded-full bg-[#B91C1C] text-white text-sm font-semibold disabled:opacity-50";

function AddUserDialog({ open, onClose, roles, req, onDone }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "staff" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await req("post", "/users", f);
      toast.success(`Akaun ${f.email} dicipta`);
      setF({ name: "", email: "", password: "", role: "staff" });
      onDone();
      onClose();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-user-dialog">
        <DialogHeader><DialogTitle>Tambah Staff</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <input required placeholder="Nama" value={f.name} onChange={set("name")} className={inputCls} data-testid="add-user-name" />
          <input required type="email" placeholder="Emel" value={f.email} onChange={set("email")} className={inputCls} data-testid="add-user-email" />
          <input required minLength={6} placeholder="Kata laluan (min 6 aksara)" value={f.password} onChange={set("password")} className={inputCls} data-testid="add-user-password" />
          <select value={f.role} onChange={set("role")} className={inputCls} data-testid="add-user-role">
            {roles.filter((r) => r !== "pending").map((r) => <option key={r} value={r}>{LABEL[r] || r}</option>)}
          </select>
          <p className="text-xs text-slate-500">Beri emel & kata laluan ini kepada staff. Mereka boleh terus log masuk di /sign-in.</p>
          <DialogFooter><button disabled={busy} className={btnCls} data-testid="add-user-submit">{busy ? "Menyimpan..." : "Cipta Akaun"}</button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetDialog({ user, onClose, req }) {
  const [pw, setPw] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try { await req("patch", `/users/${user.user_id}/password`, { password: pw }); toast.success("Kata laluan ditukar"); setPw(""); onClose(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle>Set Semula Kata Laluan</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <p className="text-sm text-slate-600">{user?.email}</p>
          <input required minLength={6} placeholder="Kata laluan baharu (min 6 aksara)" value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} data-testid="reset-password-input" />
          <DialogFooter><button className={btnCls} data-testid="reset-password-submit">Simpan</button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const IconBtn = ({ title, onClick, children, tone = "", id }) => (
  <button type="button" title={title} onClick={onClick} data-testid={id}
    className={`w-9 h-9 rounded-lg border flex items-center justify-center hover:bg-slate-50 active:scale-90 transition-transform ${tone}`}>{children}</button>
);

function UserRow({ u, me, roles, onRole, onReset, onToggle, onDelete }) {
  const self = u.user_id === me.user_id;
  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 py-3 ${u.disabled ? "opacity-60" : ""}`} data-testid={`user-row-${u.email}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{u.name || u.email}{u.disabled && <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700" data-testid={`user-disabled-${u.email}`}>Dinyahaktif</span>}</p>
        <p className="text-xs text-slate-500 truncate">{u.email}</p>
      </div>
      <div className="flex items-center gap-2">
        <select value={u.role} onChange={(e) => onRole(u, e.target.value)} data-testid={`user-role-${u.email}`} className="h-9 px-2 rounded-lg border text-sm">
          {roles.map((r) => <option key={r} value={r}>{LABEL[r] || r}</option>)}
        </select>
        {!self && <>
          <IconBtn title="Set semula kata laluan" onClick={() => onReset(u)} id={`user-reset-${u.email}`}><KeyRound className="w-4 h-4" /></IconBtn>
          <IconBtn title={u.disabled ? "Aktifkan" : "Nyahaktif"} onClick={() => onToggle(u)} id={`user-toggle-${u.email}`} tone={u.disabled ? "text-green-700" : "text-amber-600"}>
            {u.disabled ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
          </IconBtn>
          <IconBtn title="Padam" onClick={() => onDelete(u)} id={`user-delete-${u.email}`} tone="text-rose-600"><Trash2 className="w-4 h-4" /></IconBtn>
        </>}
      </div>
    </div>
  );
}

export function UsersCard({ req, me }) {
  const [data, setData] = useState({ roles: [], users: [] });
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState(null);
  const load = useCallback(() => req("get", "/users").then(setData), [req]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
    load();
  };
  const onRole = (u, role) => run(() => req("patch", `/users/${u.user_id}/role`, { role }), "Peranan dikemaskini");
  const onToggle = (u) => run(() => req("patch", `/users/${u.user_id}/active`, { active: !!u.disabled }), u.disabled ? "Akaun diaktifkan" : "Akaun dinyahaktifkan");
  const onDelete = (u) => window.confirm(`Padam akaun ${u.email}? Tindakan ini tidak boleh dibatalkan.`) && run(() => req("delete", `/users/${u.user_id}`), "Akaun dipadam");

  return (
    <section className="bg-white rounded-2xl border p-6" data-testid="users-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display font-bold flex items-center gap-2 mb-1"><Users className="w-5 h-5 text-red-700" />Pengguna</h3>
          <p className="text-xs text-slate-500">Tambah staff terus di sini, atau luluskan pekerja yang daftar sendiri di /sign-up.</p>
        </div>
        <button onClick={() => setAdding(true)} data-testid="add-user-button" className="h-9 px-4 rounded-full bg-[#B91C1C] text-white text-sm font-semibold flex items-center gap-2 shrink-0 hover:bg-[#7F1D1D] active:scale-95 transition-transform">
          <UserPlus className="w-4 h-4" />Tambah Staff
        </button>
      </div>
      <div className="divide-y">
        {data.users.map((u) => <UserRow key={u.user_id} u={u} me={me} roles={data.roles} onRole={onRole} onReset={setResetting} onToggle={onToggle} onDelete={onDelete} />)}
      </div>
      <AddUserDialog open={adding} onClose={() => setAdding(false)} roles={data.roles} req={req} onDone={load} />
      <ResetDialog user={resetting} onClose={() => setResetting(null)} req={req} />
    </section>
  );
}
